'use client';
import cn from 'classnames';

import './SummaryReadout.style.scss';

import Text from '@/components/common/Text/Text';
import { SignupState } from '@/types/Signup';
import { displayTierName } from '@/util/product_client_safe';

type SummaryReadoutProps = {
  className?: string;
  user: SignupState;
  onEdit: () => void;
};

/**
 * The membership summary at rest on mobile: a cream card pinned to the bottom
 * of the viewport showing what the applicant has, with Edit opening the full
 * panel over it. Desktop never renders this — there the panel itself lives in
 * the right column.
 *
 * Deliberately just tier and coverage, per the design. Pricing lives inside the
 * panel: the bar is a reminder of the membership being applied for, and a bar
 * that also carried Monthly and Due Now was both taller than the design and
 * busier than the one question it answers ("what am I signing up for?").
 *
 * Every membership value is defaulted (`INITIAL_SIGNUP_VALUE` ships
 * `insurance_level: 'standard'`, `addon_subscription: 'none'`), so an applicant
 * who never opens the editor still submits a valid membership.
 */
function SummaryReadout(props: SummaryReadoutProps) {
  const { className = '', user, onEdit } = props;

  const tier = [
    displayTierName(user.plan?.name ?? ''),
    user.addon_subscription === 'premier' ? 'Premier add-on' : null
  ]
    .filter(Boolean)
    .join(', ');

  const coverage = user.insurance_level === 'premium' ? 'Premium' : 'Standard';

  return (
    <div
      className={cn('SummaryReadout', className)}
      data-testid='summary-readout'
    >
      <Text
        variant='title-xxs-700-upper'
        className='SummaryReadout-title'
      >
        Membership Summary
      </Text>

      <dl className='SummaryReadout-rows'>
        <div className='SummaryReadout-row'>
          <dt>
            <Text variant='title-xxs-500-upper'>Tier:</Text>
          </dt>
          <dd data-testid='summary-readout-tier'>{tier}</dd>
        </div>

        <div className='SummaryReadout-row'>
          <dt>
            <Text variant='title-xxs-500-upper'>Coverage:</Text>
          </dt>
          <dd data-testid='summary-readout-coverage'>{coverage}</dd>
        </div>
      </dl>

      <button
        type='button'
        className='SummaryReadout-edit cursor'
        onClick={onEdit}
        data-testid='summary-edit'
      >
        <Text variant='title-xxs-500-upper'>Edit</Text>
      </button>
    </div>
  );
}

export default SummaryReadout;
